import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import ContentCard from "@/components/base/ContentCard";
import FavoriteButton from "@/components/base/FavoriteButton";
import NotFoundBlock from "@/components/base/NotFoundBlock";
import ShareModal from "@/components/base/ShareModal";
import Toast from "@/components/base/Toast";
import useFavorites from "@/hooks/useFavorites";
import usePageMeta from "@/hooks/usePageMeta";
import { varietyItems } from "@/mocks/content";
import { formatDate, formatViews } from "@/lib/format";

export default function VarietyDetailPage() {
  const { id } = useParams();
  const show = varietyItems.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [playerNotice, setPlayerNotice] = useState(false);
  const [toast, setToast] = useState("");

  usePageMeta(
    show ? `${show.title} - 综艺动态 - 红桃网` : "内容不存在 - 红桃网",
    show
      ? `${show.title}：${show.description.slice(0, 60)} 主持人 ${show.host}，${show.airTime} 在 ${show.platform} 播出。`
      : "你访问的综艺内容不存在，请返回综艺动态频道继续浏览。",
    show ? `${show.title},${show.genres.join(",")},综艺动态,红桃网` : "综艺动态,红桃网",
  );

  const related = useMemo(() => {
    if (!show) return [];
    const sameGenre = varietyItems.filter(
      (item) => item.id !== show.id && item.genres.some((genre) => show.genres.includes(genre)),
    );
    const others = varietyItems.filter(
      (item) => item.id !== show.id && !item.genres.some((genre) => show.genres.includes(genre)),
    );
    return [...sameGenre, ...others].slice(0, 3);
  }, [show]);

  if (!show) {
    return <NotFoundBlock title="节目不存在" backTo="/variety" backLabel="返回综艺动态" />;
  }

  const favorited = isFavorite(show.id);

  const handleFavorite = () => {
    toggleFavorite(show.id);
    setToast(favorited ? "已取消收藏（本地演示）" : "已加入收藏（仅保存在本机浏览器）");
  };

  return (
    <>
      <section className="relative overflow-hidden bg-foreground-950">
        <div className="absolute inset-0">
          <img src={show.cover} alt="" aria-hidden="true" className="h-full w-full object-cover object-top opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/85 to-foreground-950/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-transparent to-foreground-950/40" />
        </div>

        <div className="relative mx-auto w-full max-w-portal px-4 py-8 md:px-6 md:py-12">
          <Breadcrumbs
            tone="light"
            items={[
              { label: "首页", to: "/" },
              { label: "综艺动态", to: "/variety" },
              { label: show.title },
            ]}
          />

          <div className="mt-5 max-w-3xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-sm bg-primary-500 px-2 py-1 text-[11px] text-background-50">综艺</span>
              <span className="rounded-sm border border-background-50/25 px-2 py-1 text-[11px] text-background-100/85">
                {show.platform}
              </span>
              {show.genres.map((genre) => (
                <span
                  key={genre}
                  className="rounded-sm border border-background-50/25 px-2 py-1 text-[11px] text-background-100/85"
                >
                  {genre}
                </span>
              ))}
            </div>

            <h1 className="mt-3 font-heading text-2xl font-black leading-tight text-background-50 md:text-4xl">
              {show.title}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-100/80">{show.description}</p>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-background-100/70">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-star-line text-accent-400" />
                主持人 {show.host}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-tv-2-line text-accent-400" />
                {show.channel}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-accent-400" />
                首播 {formatDate(show.firstAirDate)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-accent-400" />
                {show.airTime}
              </span>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <FavoriteButton active={favorited} onToggle={handleFavorite} />
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-50/30 px-4 py-2 text-sm text-background-100 transition-colors hover:border-background-50/60 hover:bg-background-50/10"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
              <Link
                to="/variety"
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-50/30 px-4 py-2 text-sm text-background-100 transition-colors hover:border-background-50/60 hover:bg-background-50/10"
              >
                <i className="ri-arrow-left-line" />
                返回综艺动态
              </Link>
            </div>
            <p className="mt-2 text-[11px] text-background-100/40">
              收藏仅保存在本机浏览器 · 浏览 {formatViews(show.views)} 次（演示数据）
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-portal px-4 py-7 md:px-6 md:py-9">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-9">
          <div className="min-w-0">
            <section>
              <h2 className="flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-lg font-bold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-primary-500" />
                节目介绍
              </h2>
              <p className="mt-4 text-[15px] leading-[1.95] text-foreground-800">{show.description}</p>
              <p className="mt-3 text-[15px] leading-[1.95] text-foreground-700">
                节目每周固定在 {show.platform} 与观众见面，单期时长约 60 至 90 分钟。录制现场以真实交流为主，
                不设置刻意冲突环节，也不使用罐头笑声。节目组表示，希望把更多时间留给嘉宾自己，而不是流程。
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {show.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
                    #{tag}
                  </span>
                ))}
              </div>
            </section>

            <section className="mt-9">
              <div className="flex flex-wrap items-end justify-between gap-2 border-b border-background-200 pb-3">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <span className="h-4 w-1 rounded-full bg-primary-500" />
                  节目片段
                </h2>
                <span className="text-xs text-foreground-400">演示播放器 · 未接入真实视频接口</span>
              </div>

              <div className="relative mt-4 aspect-video w-full overflow-hidden rounded-lg bg-foreground-950">
                <img
                  src={show.cover}
                  alt={`${show.title} 片段封面`}
                  title={`${show.title} 节目片段 - 红桃网`}
                  className="h-full w-full object-cover object-top opacity-70"
                />
                <div className="absolute inset-0 bg-foreground-950/35" />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4 text-center">
                  <button
                    type="button"
                    onClick={() => setPlayerNotice(true)}
                    aria-label="播放节目片段（演示）"
                    className="flex h-14 w-14 cursor-pointer items-center justify-center rounded-full border border-background-50/40 bg-background-50/15 text-background-50 backdrop-blur-sm transition-colors hover:bg-primary-500 hover:border-primary-500 md:h-16 md:w-16"
                  >
                    <i className="ri-play-fill text-2xl md:text-3xl" />
                  </button>
                  <p className="text-xs text-background-100/75 md:text-sm">{show.title} · 精彩片段</p>
                </div>
              </div>

              {playerNotice && (
                <div className="animate-fade-in mt-3 flex items-start gap-2 rounded-md border border-dashed border-accent-300 bg-accent-50 px-4 py-3 text-xs leading-relaxed text-accent-900">
                  <i className="ri-information-line mt-0.5 shrink-0" />
                  <span>
                    演示播放器：本站未接入真实视频播放接口，点击播放不会产生实际播放行为。此处仅用于展示视频区域的界面样式。
                  </span>
                </div>
              )}
            </section>

            <section className="mt-9">
              <div className="mb-4 flex items-end justify-between gap-4 border-b border-background-200 pb-3">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <span className="h-4 w-1 rounded-full bg-primary-500" />
                  相关综艺
                </h2>
                <Link to="/variety" className="flex items-center gap-0.5 text-xs text-foreground-500 hover:text-primary-600">
                  更多节目
                  <i className="ri-arrow-right-s-line" />
                </Link>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {related.map((item) => (
                  <ContentCard key={item.id} item={item} showSummaryAlways />
                ))}
              </div>
            </section>

            <div className="mt-8 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs leading-relaxed text-foreground-500">
                <i className="ri-information-line mr-1 text-primary-500" />
                本页节目信息、播出时间与播放器均为虚构演示内容。
              </p>
              <Link
                to="/variety"
                className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-arrow-left-line" />
                返回综艺动态
              </Link>
            </div>
          </div>

          <aside className="space-y-4 self-start lg:sticky lg:top-[116px]">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-file-list-3-line text-primary-500" />
                节目信息
              </h2>
              <dl className="mt-3 space-y-2.5 text-[13px]">
                {[
                  { label: "主持人", value: show.host },
                  { label: "播出频道", value: show.channel },
                  { label: "播出平台", value: show.platform },
                  { label: "首播时间", value: formatDate(show.firstAirDate) },
                  { label: "更新时间", value: show.airTime },
                  { label: "综艺类型", value: show.genres.join(" / ") },
                ].map((row) => (
                  <div key={row.label} className="flex gap-3">
                    <dt className="w-16 shrink-0 text-foreground-400">{row.label}</dt>
                    <dd className="min-w-0 flex-1 text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50">
              <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                  <i className="ri-fire-fill text-primary-500" />
                  综艺热榜
                </h2>
                <span className="font-label text-[10px] tracking-widest text-foreground-400">热榜</span>
              </div>
              <ol className="divide-y divide-background-100 px-4">
                {[...varietyItems]
                  .sort((a, b) => b.views - a.views)
                  .slice(0, 5)
                  .map((item, index) => (
                    <li key={item.id}>
                      <Link to={`/variety/${item.id}`} className="group flex items-start gap-3 py-3">
                        <span
                          className={[
                            "flex h-5 w-5 shrink-0 items-center justify-center rounded-sm font-label text-[11px] font-bold",
                            index < 3 ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-500",
                          ].join(" ")}
                        >
                          {index + 1}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] leading-snug text-foreground-800 transition-colors group-hover:text-primary-600 line-clamp-2">
                            {item.title}
                          </span>
                          <span className="mt-1 block text-[11px] text-foreground-400">{item.airTime}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
              </ol>
            </div>
          </aside>
        </div>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={`${show.title} · ${show.platform}`} />
      {toast && <Toast message={toast} onClose={() => setToast("")} />}
    </>
  );
}